import { Box, Container } from "@material-ui/core";
import React, { useEffect } from "react";
import { TextBold } from "../../text/Text";
import "./home.scss";
import { useHistory } from "react-router-dom";

const HYRBILEN_WIDGET_SCRIPT_ID = "hyrbilen-widget-script";
const HYRBILEN_WIDGET_SCRIPT_SRC = "https://widget.hyrbilen.se/bundle.js";

const HYRBILEN_TRACKED_BOOKINGS_KEY = "hyrbilen_tracked_booking_ids";
const OLD_META_TRACKING_KEY = "meta_purchase_fired";

const HyrbilenWidget = () => {
  useEffect(() => {
    const config = {
      apiKey: "ZU0TUFo3UltKV0VoABtOQlJAVBE1FBFX",
      rootSelector: "#hyrbilen-widget-root",
      language: "sv",
      accentColor: "#ec2b34",
      buttonTextColor: "#ffffff",
      logoImageSrc: "none",
      termsUrl: "https://folkesbiluthyrning.se/faq",
      cdrUrl: "https://folkesbiluthyrning.se/images/FAQ-SKR.jpg",
      cdeUrl: "https://folkesbiluthyrning.se/images/FAQ-SKR.jpg",
      foreignPermitUrl:
        "https://folkesbiluthyrning.se/images/FAQ-K%C3%B6ra-Utomlands.jpg",
      langOverrides: {
        sv: {
          foreign_permit: "|Utlandstillstånd| *",
          collision_damage_reduction: "|Självriskreducering| *",
          collision_damage_elimination: "|Självriskeliminering| *",
          i_accept_the_terms: "Jag accepterar {villkoren}",
          book_now_alt: "Slutför Bokning",
          booking_request_successful:
            "Bokningen Lyckades! En bokningsbekräftelse har skickats till din e-postadress. Om du inte har fått mejlet inom några minuter, titta gärna i mappen för skräppost.",
        },
      },
    };

    const isCreateBookingUrl = (url) => {
      return String(url || "").includes("/api/create-booking");
    };

    const safeGetTrackedBookingIds = () => {
      try {
        const raw = sessionStorage.getItem(HYRBILEN_TRACKED_BOOKINGS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];

        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    };

    const hasTrackedBooking = (bookingId) => {
      if (!bookingId) {
        return sessionStorage.getItem(OLD_META_TRACKING_KEY) === "true";
      }

      const trackedIds = safeGetTrackedBookingIds();

      return trackedIds.includes(bookingId);
    };

    const markBookingAsTracked = (bookingId) => {
      try {
        if (!bookingId) {
          sessionStorage.setItem(OLD_META_TRACKING_KEY, "true");
          return;
        }

        const trackedIds = safeGetTrackedBookingIds();

        if (!trackedIds.includes(bookingId)) {
          trackedIds.push(bookingId);
        }

        sessionStorage.setItem(
          HYRBILEN_TRACKED_BOOKINGS_KEY,
          JSON.stringify(trackedIds)
        );
      } catch (error) {
        sessionStorage.setItem(OLD_META_TRACKING_KEY, "true");
      }
    };

    const parseRequestBody = (body) => {
      try {
        if (!body) return null;

        if (typeof body === "string") {
          return JSON.parse(body);
        }

        if (typeof FormData !== "undefined" && body instanceof FormData) {
          return null;
        }

        if (typeof Blob !== "undefined" && body instanceof Blob) {
          return null;
        }

        if (typeof body === "object") {
          return body;
        }

        return null;
      } catch (error) {
        console.warn("Could not parse Hyrbilen booking request body", error);
        return null;
      }
    };

    const extractBookingTrackingData = (body) => {
      const parsedBody = parseRequestBody(body);

      if (!parsedBody) {
        return {
          bookingId: null,
          bookingValue: null,
          vehicleCategory: null,
          vehicleName: null,
          vehiclePriceGroup: null,
        };
      }

      const bookingInfo = parsedBody?.bookingInfo;
      const vehicle = bookingInfo?.vehicle;

      const bookingValue =
        typeof vehicle?.price === "number" && !Number.isNaN(vehicle.price)
          ? vehicle.price
          : null;

      return {
        bookingId: bookingInfo?.uid || null,
        bookingValue,
        vehicleCategory: bookingInfo?.drivingExperience || null,
        vehicleName: vehicle?.makeModelYear || null,
        vehiclePriceGroup: vehicle?.priceGroup || null,
      };
    };

    const trackBookingCreated = (
      status,
      transport,
      {
        bookingId,
        bookingValue,
        vehicleCategory,
        vehicleName,
        vehiclePriceGroup,
      } = {}
    ) => {
      if (hasTrackedBooking(bookingId)) {
        console.log("Hyrbilen booking already tracked, skipping duplicate", {
          bookingId,
        });
        return;
      }

      markBookingAsTracked(bookingId);

      window.dataLayer = window.dataLayer || [];

      const eventData = {
        event: "hyrbilen_booking_created",
        booking_status: "success",
        api_status: status,

        // Booking ID / deduplication
        booking_id: bookingId,
        transaction_id: bookingId,

        // Value data
        booking_value: bookingValue,
        value: bookingValue,
        currency: "SEK",

        // Vehicle/category data
        vehicle_category: vehicleCategory,
        vehicle_name: vehicleName,
        vehicle_price_group: vehiclePriceGroup,

        // Technical/debug data
        transport,
        source: "hyrbilen_widget",
      };

      window.dataLayer.push(eventData);

      console.log("Hyrbilen booking tracked:", eventData);
    };

    const installHyrbilenTracking = () => {
      if (window.__hyrbilenTrackingInstalled) {
        console.log("Hyrbilen tracking already installed");
        return;
      }

      window.__hyrbilenTrackingInstalled = true;

      console.log("Installing Hyrbilen tracking");

      /**
       * Track fetch requests
       */
      if (typeof window.fetch === "function") {
        const originalFetch = window.fetch;

        window.fetch = async function (...args) {
          const requestUrl =
            typeof args[0] === "string" ? args[0] : args[0]?.url || "";

          const requestBody =
            args[1]?.body ||
            (typeof args[0] !== "string" ? args[0]?.body : null);

          const bookingTrackingData = extractBookingTrackingData(requestBody);

          const response = await originalFetch.apply(this, args);

          try {
            if (isCreateBookingUrl(requestUrl)) {
              console.log("Hyrbilen create-booking fetch detected", {
                requestUrl,
                status: response.status,
                ...bookingTrackingData,
              });

              const isSuccessful =
                response.status >= 200 && response.status < 300;

              if (isSuccessful) {
                trackBookingCreated(
                  response.status,
                  "fetch",
                  bookingTrackingData
                );
              }
            }
          } catch (error) {
            console.warn("Hyrbilen fetch tracking error", error);
          }

          return response;
        };
      }

      /**
       * Track XMLHttpRequest requests
       */
      if (window.XMLHttpRequest) {
        const originalOpen = XMLHttpRequest.prototype.open;
        const originalSend = XMLHttpRequest.prototype.send;

        XMLHttpRequest.prototype.open = function (method, url, ...rest) {
          this.__hyrbilenRequestUrl = url;
          this.__hyrbilenRequestMethod = method;

          return originalOpen.call(this, method, url, ...rest);
        };

        XMLHttpRequest.prototype.send = function (...args) {
          const requestBody = args[0];
          const bookingTrackingData = extractBookingTrackingData(requestBody);

          this.addEventListener("loadend", function () {
            try {
              const requestUrl = this.__hyrbilenRequestUrl || "";

              if (isCreateBookingUrl(requestUrl)) {
                console.log("Hyrbilen create-booking XHR detected", {
                  requestUrl,
                  status: this.status,
                  ...bookingTrackingData,
                });

                const isSuccessful = this.status >= 200 && this.status < 300;

                if (isSuccessful) {
                  trackBookingCreated(
                    this.status,
                    "xhr",
                    bookingTrackingData
                  );
                }
              }
            } catch (error) {
              console.warn("Hyrbilen XHR tracking error", error);
            }
          });

          return originalSend.apply(this, args);
        };
      }

      /**
       * Track sendBeacon requests, just in case.
       * Note: sendBeacon does not return a response status.
       */
      if (navigator.sendBeacon) {
        const originalSendBeacon = navigator.sendBeacon.bind(navigator);

        navigator.sendBeacon = function (url, data) {
          try {
            if (isCreateBookingUrl(url)) {
              const bookingTrackingData = extractBookingTrackingData(data);

              console.log("Hyrbilen create-booking beacon detected", {
                url,
                ...bookingTrackingData,
              });

              trackBookingCreated(
                "beacon",
                "sendBeacon",
                bookingTrackingData
              );
            }
          } catch (error) {
            console.warn("Hyrbilen beacon tracking error", error);
          }

          return originalSendBeacon(url, data);
        };
      }
    };

    const initWidget = () => {
      if (window.Hyrbilen) {
        window.Hyrbilen.initWidget(config);
      }
    };

    /**
     * Important:
     * Install tracking before loading/initing the widget.
     */
    installHyrbilenTracking();

    const existingScript = document.getElementById(HYRBILEN_WIDGET_SCRIPT_ID);

    if (existingScript) {
      initWidget();
      existingScript.addEventListener("load", initWidget);

      return () => {
        existingScript.removeEventListener("load", initWidget);
      };
    }

    const script = document.createElement("script");
    script.id = HYRBILEN_WIDGET_SCRIPT_ID;
    script.src = HYRBILEN_WIDGET_SCRIPT_SRC;
    script.async = true;
    script.addEventListener("load", initWidget);

    script.addEventListener("error", () => {
      console.warn("Hyrbilen widget script failed to load");

      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: "hyrbilen_widget_script_error",
        source: "hyrbilen_widget",
      });
    });

    document.body.appendChild(script);

    return () => {
      script.removeEventListener("load", initWidget);
    };
  }, []);

  return <div id="hyrbilen-widget-root" className="hyrbilenWidgetRoot" />;
};

/**
 * Homepage
 * @author Erik Jonasson
 */
const Home = () => {
  const history = useHistory();

  useEffect(() => {
    return history.listen((newLocation, action) => {
      if (action === "POP") {
        history.push("/");
      }
    });
  }, [history]);

  useEffect(() => {
    document.title = "Folkes biluthyrning";
  }, []);

  useEffect(() => {
    sessionStorage.removeItem(OLD_META_TRACKING_KEY);
    sessionStorage.removeItem(HYRBILEN_TRACKED_BOOKINGS_KEY);
  }, []);

  return (
    <div className="home">
      <div className="homeImage" />
      <Container maxWidth="md">
        <Box
          color="#FFF"
          fontSize={["20px", "26px", "35px"]}
          textAlign={["center", "left"]}
          mt={["200px", "200px", "350px"]}
        >
          <TextBold mb={["25px", "30px", "45px"]}>
            Din lokala biluthyrare med det breda utbudet. Nu hittar ni oss i
            våra nya lokaler på Sellerigatan 5 i Lund!
          </TextBold>
        </Box>
      </Container>

      <Container maxWidth="lg">
        <Box className="hyrbilenWidgetSection" mb={["60px", "90px"]}>
          <HyrbilenWidget />
        </Box>
      </Container>
    </div>
  );
};

export default Home;